import { inject, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    defer,
    filter,
    map,
    throwError,
} from 'rxjs';
import { IPost, IPostData } from './post.interface';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG } from '../../app.config';
import { environment } from '../../../environments/environment';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { PathsService } from '../paths/paths.service';
import { POST_NODE } from '../../../../graphQL/node-query-strings';
import { finalize } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class BlogService {
    private readonly http = inject(HttpClient);
    private readonly appConfig = inject(APP_CONFIG);
    public loading$ = new BehaviorSubject(true);
    private readonly apolloProvider = inject(Apollo);
    private readonly paths = inject(PathsService);
    private readonly apollo: ApolloBase | null;

    constructor() {
        this.apollo = this.appConfig.useJson
            ? null
            : this.apolloProvider.use('cms');
    }

    public getPosts$(cat: string): Observable<IPost[]> {
        if (this.appConfig.useJson) {
            return this.getJsonPosts$(cat);
        } else {
            return this.getPostFromCMS$(cat);
        }
    }

    private getPostFromCMS$(getCat: string): Observable<IPost[]> {
        return defer(() => {
            this.loading$.next(true);
            return this.getGraphQlPostData().pipe(
                map((result) => result.data?.posts?.nodes ?? []),
                map((nodes) => this.filterPostsByCategory(getCat, nodes)),
                finalize(() => this.loading$.next(false)),
            );
        });
    }

    private getJsonPosts$(getCat: string) {
        return this.http
            .get<IPostData>(
                `${this.appConfig.assetsPath}${this.appConfig.json.posts}`,
            )
            .pipe(
                filter((data) => !!data),
                map((data) => data.data.data),
                map((info) => this.getCategory(getCat, info)),
                map((posts: IPost[]) => {
                    posts.forEach((post: IPost) => {
                        // Convert date to ISO string
                        post.date = new Date(post.date).toISOString();
                        // Convert content to string
                        post.content = post.content
                            ? this.paths.cleanPage(
                                  environment.version,
                                  post.content,
                                  '',
                                  '',
                                  false,
                              )
                            : '';
                        // Ensure categories is an array

                        return post;
                    });

                    return posts;
                }),
            );
    }

    private filterPostsByCategory(catName: string, nodes: IPost[]): IPost[] {
        return nodes.filter((node) =>
            node.categories.nodes.some((cat) => cat.name === catName),
        );
    }

    private getCategory(catName: string, data: { posts: { nodes: IPost[] } }) {
        this.loading$.next(false);

        return data.posts.nodes.filter((node: any) =>
            node.categories.nodes.find((cat: any) => cat.name === catName),
        );
    }

    private getGraphQlPostData(): Observable<
        Apollo.QueryResult<{ posts: { nodes: IPost[] } }>
    > {
        if (!this.apollo) {
            return throwError(
                () =>
                    new Error(
                        'CMS Apollo client not initialized. Ensure useJson is false before calling getPostFromCMS$.',
                    ),
            );
        }

        return this.apollo.query<{ posts: { nodes: IPost[] } }>({
            query: gql`
                {
                    posts {
                        nodes {
                            ${POST_NODE}
                        }
                    }
                }
            `,
        });
    }
}
